import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, Observable, of, forkJoin, map, switchMap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { ProductTranslationService, PRODUCT_NAMESPACE_RTPC_FIELD } from '../../../services/product-translation.service';
import { ConfigurationService } from '../../../services/configuration.api.service';
import { ProductType } from '../../../models/product';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { UserService } from '../../../../shared/services/user.service';
import { AppOfferService } from '../../../../offer/services/app-offer.service';
import { AppWizardService } from '../../../../offer/services/app-wizard.service';
import { User } from '../../../../shared/models/user';
import { ProductService } from '../../../services/product.api.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { safe } from '../../../../shared/utils/safe';
import { TranslationService } from '../../../../shared/services/translation.service';
import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { ScrollerDirective } from '../../../../shared/directives/scroller.directive';

import { buildProductCardFields } from './utils/product-card-fields-builder';
import { FieldObjectCst } from './utils/field/field-object';
import { ProductCardContentComponent } from './product-card-content.component';

interface CustomType {
	cardfeasibility: string;
	wizard: boolean;
	productRef: string;
	productType: ProductType;
	configurationRef: string;
	soldTo: string;
	shipTo: string;
	endUser: string;
	serviceType: string;
}

@Component({
	selector: 'copo-offer-product-card',
	templateUrl: './offer-product-card-component.html',
	styleUrl: './offer-product-card-component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		LoadingSpinnerComponent,
		BackButtonComponent,

		MatButtonModule,
		TranslateModule,
		ProductCardContentComponent,
	],
})
export class OfferProductCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	user!: User;
	isLoading = true;
	isError = false;

	userPartReference: string | undefined;
	subcategory?: string;
	form?: string;
	isfeasibility = false;
	isWizard = false;
	productRef = '';
	productType: ProductType = 'a2c';
	configurationRef: string | undefined;
	productRefId = '';
	soldTo!: string;
	shipTo!: string;
	endUser!: string;
	serviceType!: string;
	userBocData?: SoldToRelationshipGroup[];
	bocs?: Map<string, BocClient>;
	productFieldsObs?: Observable<FieldObjectCst>;
	productConfiguration!: Subscription;
	bocsOfferList!: Subscription;
	soldToBocList?: BocClient[];
	soldToList?: string[];
	constructor(
		private readonly productService: ProductService,
		private readonly configurationService: ConfigurationService,
		private readonly productTranslationService: ProductTranslationService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly appOfferService: AppOfferService,
		private readonly appWizardService: AppWizardService,
		private readonly router: Router,
		private readonly route: ActivatedRoute
	) {
		this.getInformationFromState();
		this.getBocsOfferList();
	}

	getInformationFromState() {
		const { state } = <{ state: unknown }> history;
		if (state !== null && typeof state === 'object') {
			const { cardfeasibility, wizard, productRef, productType, soldTo, shipTo,
				endUser, configurationRef, serviceType } = <Partial<CustomType>> state;
			this.isfeasibility = Boolean(cardfeasibility === 'YES');
			this.isWizard = Boolean(wizard);
			this.productRef = String(productRef);
			this.soldTo = String(soldTo);
			this.shipTo = String(shipTo);
			this.endUser = String(endUser);
			if (productType) {
				this.productType = productType;
			} else if (this.route.snapshot.queryParams['productType']) {
				this.productType = <ProductType> this.route.snapshot.queryParams['productType'];
			}
			if (configurationRef) {
				this.configurationRef = configurationRef;
			} else if (this.route.snapshot.queryParams['configurationRef']) {
				this.configurationRef = String(this.route.snapshot.queryParams['configurationRef']);
			}
			if (serviceType) {
				this.serviceType = String(serviceType);
			} else if (this.route.snapshot.queryParams['serviceType']) {
				this.serviceType = String(this.route.snapshot.queryParams['serviceType']);
			}
		}
	}

	ngOnDestroy() {
		this.productConfiguration.unsubscribe();
	}

	translate(value: string) {
		return this.productTranslationService.translate(`${ PRODUCT_NAMESPACE_RTPC_FIELD }${ value }`);
	}

	async duplicationAction() {
		const productReference = this.productRef;
		await this.router
			.navigateByUrl(
				`product/template/${ productReference }/${ this.productType }/${ this.productRefId }`,
				{ state: { shipTo: this.shipTo,
					soldTo: this.soldTo,
					endUser:	this.endUser,
					serviceType: this.serviceType } }
			);
	}

	private getBocsOfferList() {
		this.bocsOfferList = forkJoin([
			this.userService.getSoldToRelationshipGroupsTransac(this.serviceType)
				.pipe(switchMap(userBocData => {
					if (userBocData.length === 0) {
						return of(<const> [userBocData, <BocClient[]> []]);
					}
					const businessIds = Array.from(
						new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers]))
					);
					return forkJoin([
						of(userBocData),
						this.userService.getBocs(
							['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
							businessIds.map(x => `businessId:${ x }`).join(' OR ')
						).pipe(map(v => v.results))
					]);
				}))
		]).subscribe({
			next: ([[userBocData, bocs]]) => {
				this.userBocData = userBocData;
				this.isLoading = false;
				const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
				this.soldToList = this.userBocData.map(v => v.soldTo);
				this.soldToBocList = this.soldToList.map(v => safe(bocsMap.get(v)));
				this.loadConfiguration();
			},
			error: (err: unknown) => {
				console.error('error loading wizard parameters', err);
				this.isLoading = false;
				this.appWizardService.getState().errorInPage = true;
				this.appOfferService.showError(
					this.translationService.getTR('eoffer.wizard.parameters.error')
				);
			},
		});
	}

	private getBocsUserValue(userValue: string, userList: BocClient[]) {
		for (const list of userList) {
			if (list.businessId === userValue) {
				return `${ list.name } / ${ list.city }`;
			}
		}
		return '';
	}

	private getSoldToList() {
		return this.soldToBocList !== undefined ? this.soldToBocList : [];
	}

	private getShipToList() {
		const soldToBusinessId = this.soldTo;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	private getEndUserList() {
		const soldToBusinessId = this.soldTo;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.endUsers.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	private loadConfiguration() {
		this.user = this.userService.user;
		this.productRefId = this.configurationRef || this.productRef;
		this.productConfiguration = this.configurationService
			.getConfigurationMappedOrNot(this.productRefId, this.productType, 'INLINE')
			.subscribe({
				next: product => {
					this.isLoading = false;
					this.userPartReference = product.usePartReference;
					this.subcategory = product.subcategory || UNDERSCORE;
					this.form = product.form || UNDERSCORE;
					this.productFieldsObs = of(buildProductCardFields(
						product,
						this.getBocsUserValue(this.soldTo, this.getSoldToList()),
						this.getBocsUserValue(this.shipTo, this.getShipToList()),
						this.getBocsUserValue(this.endUser, this.getEndUserList())
					));
				},
				error: (err: unknown) => {
					console.error('unable to load configurationa and field names', err);
					this.productService.showError();
					this.isLoading = false;
					this.isError = true;
				}
			});
	}
}
